import ReactEChartsCore from 'echarts-for-react/lib/core';
import throttle from 'lodash/throttle';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { EChartsOption, ECharts } from 'echarts';

import echarts from './echartsCore';
import useEChartsTheme from './useEChartsTheme';

// ECharts' own finder/value types are internal to its .d.ts, so derive what we can and keep the
// value type permissive (ScaleDataValue is effectively number | string | Date).
export type ChartFinder = Parameters<ECharts['convertFromPixel']>[0];
export type ChartDataValue = number | string | Date;

/**
 * The readiness-safe imperative surface every ordinary chart consumer should use.
 *
 * echarts-for-react hands out a *temporary*, model-less ECharts instance between mount and
 * onChartReady (it inits, waits for 'finished', disposes, then re-inits at the measured size —
 * see echarts-for-react/lib/core.js initEchartsInstance). Model-dependent APIs throw on that
 * instance, and the same window reopens on every re-initialization. Every method here degrades
 * to null for that frame instead of throwing; pair with `subscribeReady` (or `useChartAnchor`)
 * to recompute once the chart is genuinely ready.
 */
export type ChartCoordinateApi = {
  isReady: () => boolean;
  // null = unavailable this frame (pre-ready, re-initializing, or disposed) — never a throw.
  convertToPixel: (finder: ChartFinder, value: ChartDataValue | ChartDataValue[]) => number | number[] | null;
  convertFromPixel: (finder: ChartFinder, pixel: number[]) => number[] | null;
  // Deliberately `boolean | null`, not `false`, so callers can tell "chart unavailable" from
  // "point is outside the grid". Collapsing the two silently corrupts geometry searches.
  containPixel: (finder: ChartFinder, pixel: number[]) => boolean | null;
  getSize: () => { width: number; height: number } | null;
  // Fires whenever the chart (re)becomes ready. Returns an unsubscribe function.
  subscribeReady: (listener: () => void) => () => void;
};

export type EChartsBaseHandle = ChartCoordinateApi & {
  dispatchAction: (action: { type: string } & Record<string, unknown>) => void;
  getDom: () => HTMLElement | undefined;
  // Escape hatch for needs the narrow API cannot express (ZRender overlays). Runs `fn` only
  // against a live, non-disposed instance and swallows lifecycle throws, returning undefined.
  // Prefer the methods above; see utils/chart-options/CLAUDE.md before reaching for this.
  withChart: <T>(fn: (chart: ECharts) => T) => T | undefined;
};

interface EChartsBaseProps {
  option: EChartsOption;
  onEvents?: Record<string, (params: any) => void>;
  onChartReady?: (instance: ECharts) => void;
  height?: number | string;
  width?: number | string;
  autoResize?: boolean;
  style?: React.CSSProperties;
  notMerge?: boolean; // false = merge new option into existing chart state (preserves zoom/selection)
  renderer?: 'svg' | 'canvas';
  testId?: string;
  loading?: boolean;
}

const LOADING_INDICATOR_DELAY_MS = 150;

const EChartsBase = forwardRef<EChartsBaseHandle, EChartsBaseProps>(function EChartsBase(
  {
    option,
    onEvents,
    onChartReady,
    height = 400,
    width,
    autoResize = true,
    style,
    notMerge = false,
    renderer = 'svg',
    testId,
    loading = false,
  },
  ref,
) {
  const coreRef = useRef<InstanceType<typeof ReactEChartsCore>>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);
  // The instance we last saw reach onChartReady. Readiness is checked by *identity* against the
  // instance currently bound to the DOM, never by a boolean flag: echarts-for-react disposes and
  // re-inits inside its own componentDidUpdate (whenever theme or opts deep-change), which React
  // commits before any ancestor effect
  // runs, so a flag cleared from an effect here would read stale-true over the fresh model-less
  // instance — exactly the window we are guarding.
  const readyInstanceRef = useRef<ECharts | null>(null);
  // Must live in a ref: useImperativeHandle below has no dependency array, so the handle object
  // is rebuilt on every render and a Set created in the factory would drop every subscription.
  const readyListenersRef = useRef<Set<() => void>>(new Set());
  const { fontFamily } = useEChartsTheme();

  // Resolved lazily on every call and never cached, so a consumer can never be handed an
  // instance that has since been replaced or disposed.
  const getLiveInstance = useCallback((): ECharts | undefined => {
    const instance = coreRef.current?.getEchartsInstance();
    return instance && !instance.isDisposed() ? instance : undefined;
  }, []);

  const runSafely = useCallback(
    <T,>(fn: (chart: ECharts) => T): T | undefined => {
      const instance = getLiveInstance();
      if (!instance) return undefined;
      try {
        return fn(instance);
      } catch {
        // Model-dependent ECharts APIs throw while a chart is initializing or re-initializing
        // (its internal model does not exist yet). Degrade for this frame; callers recover via
        // subscribeReady / useChartAnchor, or simply on the next pointer event.
        return undefined;
      }
    },
    [getLiveInstance],
  );

  useImperativeHandle(ref, () => ({
    isReady: () => {
      const instance = getLiveInstance();
      return !!instance && instance === readyInstanceRef.current;
    },
    convertToPixel: (finder, value) => runSafely((chart) => chart.convertToPixel(finder, value)) ?? null,
    convertFromPixel: (finder, pixel) => runSafely((chart) => chart.convertFromPixel(finder, pixel)) ?? null,
    containPixel: (finder, pixel) => runSafely((chart) => chart.containPixel(finder, pixel)) ?? null,
    getSize: () => runSafely((chart) => ({ width: chart.getWidth(), height: chart.getHeight() })) ?? null,
    subscribeReady: (listener) => {
      readyListenersRef.current.add(listener);
      return () => {
        readyListenersRef.current.delete(listener);
      };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatchAction: (action) => void runSafely((chart) => chart.dispatchAction(action as any)),
    getDom: () => runSafely((chart) => chart.getDom()),
    withChart: (fn) => runSafely(fn),
  }));

  // Own the resize instead of echarts-for-react's built-in autoResize (disabled below). Its
  // size-sensor uses a 60ms *trailing* debounce, so during a continuous window drag
  // chart.resize() never fires until the drag stops — the canvas keeps stale pixel dimensions
  // inside its width:100% element, overflows/shifts, then snaps back at the end (SIMPL-34535).
  // We instead resize on a leading+trailing throttle (~2 frames) so the chart tracks its
  // element as it drags, while still capping the (expensive) relayout+repaint below per-frame.
  // NOTE: resize() must be passed { width: 'auto', height: 'auto' } — a bare resize() call does
  // NOT re-measure the DOM in echarts v6 and is a no-op. This mirrors what echarts-for-react's
  // own (debounced) autoResize does.
  const throttledResize = useMemo(
    () => throttle(() => coreRef.current?.getEchartsInstance()?.resize({ width: 'auto', height: 'auto' }), 32),
    [],
  );

  const handleChartReady = useCallback(
    (instance: ECharts) => {
      setChartInstance(instance);
      readyInstanceRef.current = instance;
      resizeObserverRef.current?.disconnect();
      if (autoResize) {
        const observer = new ResizeObserver(() => throttledResize());
        observer.observe(instance.getDom());
        resizeObserverRef.current = observer;
      }
      onChartReady?.(instance);
      // Notified last, so subscribers see a fully wired chart. Fires again on every re-init,
      // which is how overlays anchored to data coordinates recover after one. Each listener is
      // isolated: this runs inside echarts-for-react's awaited initialization, so one throwing
      // subscriber would otherwise skip every listener after it and surface as an unhandled
      // rejection rather than as an error anyone can attribute.
      readyListenersRef.current.forEach((listener) => {
        try {
          listener();
        } catch {
          // A subscriber's own failure must not break the chart or its sibling subscribers.
        }
      });
    },
    [autoResize, onChartReady, throttledResize],
  );

  useEffect(() => {
    if (!chartInstance) return undefined;

    if (!loading) {
      chartInstance.hideLoading();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      chartInstance.showLoading('default', { text: '' });
    }, LOADING_INDICATOR_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      chartInstance.hideLoading();
    };
  }, [chartInstance, loading]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      throttledResize.cancel();
      readyInstanceRef.current = null;
    },
    [throttledResize],
  );

  // Default every chart's text to Simplifi's font (MUI theme) so all ECharts charts share the
  // app typography. A chart's own option.textStyle still wins where it sets specific values.
  const themedOption = useMemo(() => {
    const existing = (option as { textStyle?: Record<string, unknown> }).textStyle;
    return { ...option, textStyle: { fontFamily, ...existing } };
  }, [option, fontFamily]);

  return (
    <ReactEChartsCore
      key={renderer}
      ref={coreRef}
      echarts={echarts}
      option={themedOption}
      onEvents={onEvents}
      onChartReady={handleChartReady}
      opts={{
        renderer,
        ...(typeof width === 'number' ? { width } : {}),
        ...(typeof height === 'number' ? { height } : {}),
      }}
      notMerge={notMerge}
      lazyUpdate={false}
      autoResize={false}
      style={{ width: '100%', height, ...style }}
      {...(testId ? { 'data-testid': testId } : {})}
    />
  );
});

export default EChartsBase;
